import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { Wrench, Lightbulb, Pencil, X, Sparkles } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { ThemedText, Button, FAQModal, PageHeader, GenerateDemoDataDialog } from '../components';
import { Layout } from '../components/Layout';
import { FormBuilder } from '../components/FormBuilder/FormBuilder';
import { ModalOverlay as Modal, ModalContent, ModalHeader } from '../components/ModalStyles';

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

// Header components replaced by global PageHeader

export const FormBuilderPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [warningOpen, setWarningOpen] = useState(!!id && !location.state?.skipWarning);
  const [isDemoDataOpen, setIsDemoDataOpen] = useState(false);

  // "Generate demo data" is for demo-org builders only, and only once the form is saved (has an id).
  // Read-only demo viewers are excluded — the commit endpoint would 403 for them anyway.
  const canGenerateDemoData = !!id && user?.isDemoOrg === true && user?.isReadOnlyDemo !== true;

  return (
    <Layout currentPath="/form-builder">
      <Helmet>
        <title>Form Builder - aQRate Portal</title>
        <meta name="description" content="Create custom water quality observation forms with mobile-optimized components" />
      </Helmet>

      <PageContainer theme={theme}>
        <PageHeader
          theme={theme}
          title="Form Builder"
          subtitle="Design and customize mobile-first data collection forms for your field teams."
          icon={<Wrench size={20} />}
          actions={
            <>
              {canGenerateDemoData && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setIsDemoDataOpen(true)}
                  title="Generate realistic demo submissions for this form"
                >
                  <Sparkles size={14} /> Generate demo data
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsFaqModalOpen(true)}
                title="Get help and view FAQs"
              >
                <Lightbulb size={14} /> Help & Guide
              </Button>
            </>
          }
        />

        <FormBuilder />
      </PageContainer>

      <FAQModal
        isOpen={isFaqModalOpen}
        onClose={() => setIsFaqModalOpen(false)}
        organizationId={user?.organizationId}
      />

      {canGenerateDemoData && (
        <GenerateDemoDataDialog
          formSchemaId={id}
          formName={location.state?.formData?.name}
          open={isDemoDataOpen}
          onClose={() => setIsDemoDataOpen(false)}
          onCommitted={() => setIsDemoDataOpen(false)}
        />
      )}

      {/* Edit Form Warning Modal */}
      <Modal theme={theme} isOpen={warningOpen}>
        <ModalContent theme={theme} style={{ maxWidth: 480 }}>
          <ModalHeader theme={theme}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Pencil size={18} color="#d97706" />
              </div>
              <div>
                <ThemedText type="subtitle" style={{ margin: 0 }}>Edit Form Structure</ThemedText>
                <ThemedText type="small" style={{ color: theme.colors.muted, margin: 0 }}>Review before making changes</ThemedText>
              </div>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate(-1)} style={{ padding: '4px 8px', minWidth: 'auto' }}>
              <X size={14} />
            </Button>
          </ModalHeader>
          <div style={{ padding: '0 0 24px 0' }}>
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d',
              borderLeft: '4px solid #f59e0b', borderRadius: 10,
              padding: '14px 16px', marginBottom: 16,
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 600, fontSize: 13, color: '#92400e' }}>
                Editing this form may affect how existing submissions are displayed
              </p>
              <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                If you remove or rename a field, previously collected data for that field will still be stored but may not be visible when viewing existing submissions. New submissions will use the updated form structure.
              </p>
            </div>
            <p style={{ margin: '0 0 20px 0', fontSize: 13, color: theme.colors.muted, lineHeight: 1.65 }}>
              You can safely reorder fields, change labels, or add new ones without affecting existing submission data. If you need to restore a removed field, re-add it with the same field key to make historical data visible again.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setWarningOpen(false)}>
                <Pencil size={14} /> I understood
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </Layout>
  );
};